import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { SupplierSelect } from '@/components/suppliers/SupplierSelect.js';
import {
  mergeInventoryItems,
  previewInventoryMerge,
  type InventoryDuplicateCard,
  type SupplierAssignment,
} from '@/lib/api/inventory-merge.js';
import { listInventoryItems } from '@/lib/api/inventory.js';
import { formatCurrency, formatDate } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { ManagerOverrideModal } from '@/components/common/ManagerOverrideModal.js';
import { useManagerOverride } from '@/hooks/useManagerOverride.js';

const FIELDS: Array<{ key: string; label: string }> = [
  { key: 'name', label: 'Name' },
  { key: 'sku', label: 'SKU' },
  { key: 'category', label: 'Category' },
  { key: 'condition', label: 'Condition' },
  { key: 'retailPrice', label: 'Retail price' },
  { key: 'minimumPrice', label: 'Minimum price' },
  { key: 'upc', label: 'UPC' },
  { key: 'manufacturerPartNumber', label: 'MPN' },
  { key: 'color', label: 'Color' },
  { key: 'description', label: 'Description' },
];

type DraftSupplier = SupplierAssignment & { key: string; label: string };

export function MergeInventoryModal({
  open,
  items,
  seedId,
  gradeReasons,
  onClose,
  onMerged,
}: {
  open: boolean;
  items: InventoryDuplicateCard[];
  seedId?: string;
  gradeReasons?: string[];
  onClose: () => void;
  onMerged: () => void;
}) {
  const { requireOverride, modalProps } = useManagerOverride();
  const [extraIds, setExtraIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [primaryId, setPrimaryId] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [suppliers, setSuppliers] = useState<DraftSupplier[]>([]);
  const [ackGrade, setAckGrade] = useState(false);
  const [ackCondition, setAckCondition] = useState(false);
  const [reason, setReason] = useState('');

  const ids = useMemo(() => {
    const fromItems = items.map((item) => item.id);
    return [...new Set([...fromItems, ...extraIds, ...(seedId ? [seedId] : [])])];
  }, [items, extraIds, seedId]);

  const survivorDefault = useMemo(() => {
    const sorted = [...items].sort(
      (a, b) => Number(b.qtyOnHand) - Number(a.qtyOnHand) || a.createdAt.localeCompare(b.createdAt),
    );
    return sorted[0]?.id ?? seedId ?? '';
  }, [items, seedId]);

  useEffect(() => {
    if (!open) return;
    setPrimaryId(survivorDefault);
    setFields({});
    setAckGrade(false);
    setAckCondition(false);
    setReason('');
    setExtraIds([]);
    const drafts: DraftSupplier[] = [];
    for (const item of items) {
      if (item.suppliers.length === 0) {
        drafts.push({
          key: `new-${item.id}`,
          label: item.name,
          supplierId: item.suggestedSupplier?.supplierId ?? '',
          supplierSku: item.sku ?? '',
          costPrice: Number(item.costPrice) || 0,
          isPreferred: drafts.length === 0,
        });
      } else {
        for (const row of item.suppliers) {
          drafts.push({
            key: `${item.id}-${row.supplierId}-${row.supplierSku ?? ''}`,
            label: `${item.name} · ${row.supplierName}`,
            supplierId: row.supplierId,
            supplierSku: row.supplierSku ?? '',
            costPrice: Number(row.costPrice) || 0,
            isPreferred: row.isPreferred,
          });
        }
      }
    }
    const preferredIndex = Math.max(drafts.findIndex((row) => row.isPreferred), 0);
    setSuppliers(drafts.map((row, index) => ({ ...row, isPreferred: drafts.length > 0 && index === preferredIndex })));
  }, [open, items, survivorDefault]);

  const previewQuery = useQuery({
    queryKey: ['inventory-merge-preview', primaryId, ids],
    queryFn: () =>
      previewInventoryMerge({
        primaryId,
        duplicateIds: ids.filter((id) => id !== primaryId),
        fieldSelections: fields,
      }),
    enabled: open && Boolean(primaryId) && ids.length >= 2,
  });

  const searchQuery = useQuery({
    queryKey: ['inventory-merge-search', search],
    queryFn: () => listInventoryItems({ search, pageSize: 8 }),
    enabled: open && search.trim().length >= 2,
  });

  const preview = previewQuery.data;
  const previewItems = preview?.items ?? [];
  const reasons = preview?.gradeReasons ?? gradeReasons ?? [];

  function valueOf(itemId: string, key: string): string {
    const row = previewItems.find((item) => item.id === itemId) as Record<string, unknown> | undefined;
    const raw = row?.[key];
    if (raw == null || raw === '') return '—';
    if (key === 'retailPrice' || key === 'minimumPrice') return formatCurrency(String(raw));
    return String(raw);
  }

  const mergeMutation = useMutation({
    mutationFn: async () => {
      const pin = await requireOverride({
        action: 'inventory.merge',
        title: 'Manager PIN',
        description: 'Merging inventory items sums stock and cannot be casual. A manager PIN is required.',
        requiredRole: 'store_manager',
      });
      if (!pin.approved) throw new Error('Manager PIN cancelled');
      const assignments = suppliers.filter((row) => row.supplierId);
      return mergeInventoryItems({
        primaryId,
        duplicateIds: ids.filter((id) => id !== primaryId),
        fieldSelections: fields,
        supplierSkuHandling: assignments.length ? 'manual' : 'keep_all',
        supplierSkuAssignments: assignments.map((row) => ({
          supplierId: row.supplierId,
          supplierSku: row.supplierSku || undefined,
          costPrice: row.costPrice,
          isPreferred: row.isPreferred,
        })),
        reason,
        managerOverrideAuthorizedBy: pin.authorizedBy.id,
        acknowledgeGradeWarning: reasons.length > 0 ? ackGrade : undefined,
        acknowledgeConditionMismatch: preview?.conditionsDiffer ? ackCondition : undefined,
      });
    },
    onSuccess: () => {
      toastSuccess('Items merged. Stock layers kept their original costs.');
      onMerged();
    },
    onError: (error) => {
      if (error instanceof Error && error.message === 'Manager PIN cancelled') return;
      toastError(parseApiError(error).message);
    },
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/40 p-4">
      <form
        className="my-6 w-full max-w-3xl space-y-4 rounded-2xl bg-white p-6 shadow-2xl"
        onSubmit={(event) => {
          event.preventDefault();
          mergeMutation.mutate();
        }}
      >
        <div>
          <h3 className="text-lg font-semibold text-slate-950">Review merge</h3>
          <p className="text-sm text-slate-500">
            Quantities are added. Each stock layer keeps its cost and the date it was received.
          </p>
        </div>

        {reasons.length > 0 ? (
          <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            <p className="font-semibold">These may be different GRADES, not different suppliers. Merging will lose grade distinction.</p>
            <ul className="mt-1 list-disc pl-5">
              {reasons.map((reasonText) => (
                <li key={reasonText}>{reasonText}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {seedId && items.length < 2 ? (
          <div>
            <input
              className="w-full rounded-xl border px-3 py-2 text-sm"
              placeholder="Search for the other item"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <ul className="mt-2 divide-y rounded-xl border">
              {(searchQuery.data?.data ?? []).filter((row) => !ids.includes(row.id)).map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
                    onClick={() => setExtraIds((current) => [...current, row.id])}
                  >
                    <span>{row.name}</span>
                    <span className="text-slate-500">{row.sku ?? ''}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <fieldset>
          <legend className="text-sm font-semibold text-slate-800">Survivor</legend>
          <div className="mt-2 space-y-1">
            {(previewItems.length ? previewItems : items).map((item) => (
              <label key={item.id} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="survivor"
                  checked={primaryId === item.id}
                  onChange={() => setPrimaryId(item.id)}
                />
                <span className="font-medium">{item.name}</span>
                <span className="text-slate-500">qty {item.qtyOnHand}</span>
              </label>
            ))}
          </div>
        </fieldset>

        {previewItems.length >= 2 ? (
          <div className="overflow-x-auto rounded-xl border">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2">Field</th>
                  {previewItems.map((item) => (
                    <th key={item.id} className="px-3 py-2">{item.sku || item.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {FIELDS.map((field) => (
                  <tr key={field.key}>
                    <td className="px-3 py-2 font-medium">{field.label}</td>
                    {previewItems.map((item) => (
                      <td key={item.id} className="px-3 py-2">
                        <label className="flex items-start gap-2">
                          <input
                            type="radio"
                            name={field.key}
                            checked={(fields[field.key] ?? primaryId) === item.id}
                            onChange={() => setFields((current) => ({ ...current, [field.key]: item.id }))}
                          />
                          <span>{valueOf(item.id, field.key)}</span>
                        </label>
                      </td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td className="px-3 py-2 font-medium">Cost</td>
                  <td className="px-3 py-2 text-slate-600" colSpan={previewItems.length}>
                    Recomputed from stock layers{preview?.weightedAvgCost ? `: ${formatCurrency(preview.weightedAvgCost)}` : ''}. Not chosen here.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : null}

        <div>
          <h4 className="text-sm font-semibold text-slate-800">Supplier SKUs</h4>
          <p className="text-xs text-slate-500">Exactly one preferred supplier remains on the survivor.</p>
          <div className="mt-2 space-y-2">
            {suppliers.map((row) => (
              <div key={row.key} className="grid gap-2 rounded-xl border p-3 sm:grid-cols-[1fr_140px_100px_auto]">
                <div>
                  <div className="text-xs text-slate-500">{row.label}</div>
                  <SupplierSelect
                    value={row.supplierId}
                    onChange={(supplierId) =>
                      setSuppliers((current) => current.map((entry) => (entry.key === row.key ? { ...entry, supplierId } : entry)))
                    }
                  />
                </div>
                <input
                  className="rounded-xl border px-3 py-2 text-sm"
                  placeholder="Supplier SKU"
                  value={row.supplierSku ?? ''}
                  onChange={(event) =>
                    setSuppliers((current) =>
                      current.map((entry) => (entry.key === row.key ? { ...entry, supplierSku: event.target.value } : entry)),
                    )
                  }
                />
                <input
                  className="rounded-xl border px-3 py-2 text-sm"
                  type="number"
                  step="0.01"
                  value={row.costPrice ?? 0}
                  onChange={(event) =>
                    setSuppliers((current) =>
                      current.map((entry) =>
                        entry.key === row.key ? { ...entry, costPrice: Number(event.target.value) } : entry,
                      ),
                    )
                  }
                />
                <label className="flex items-center gap-1 text-xs font-semibold">
                  <input
                    type="radio"
                    name="preferred-supplier"
                    checked={Boolean(row.isPreferred)}
                    onChange={() =>
                      setSuppliers((current) => current.map((entry) => ({ ...entry, isPreferred: entry.key === row.key })))
                    }
                  />
                  Preferred
                </label>
              </div>
            ))}
          </div>
        </div>

        {preview ? (
          <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700">
            <p className="font-semibold text-slate-950">After merge</p>
            <p>Combined qty {preview.combinedQty}. Weighted average {preview.weightedAvgCost ? formatCurrency(preview.weightedAvgCost) : 'unchanged'}.</p>
            <p>{preview.layers.length} stock layers stay in received-date order.</p>
            <ul className="mt-1 max-h-28 overflow-auto text-xs text-slate-600">
              {preview.layers.map((layer) => (
                <li key={layer.id}>
                  {layer.itemName} · {layer.qtyRemaining} @ {formatCurrency(layer.unitCost)} · {formatDate(layer.receivedAt)}
                </li>
              ))}
            </ul>
            <p className="mt-1">
              {preview.compatibleModels.length} compatible devices. {preview.ticketCount} tickets and {preview.poCount} purchase orders will point at the survivor.
            </p>
          </div>
        ) : null}

        {reasons.length > 0 ? (
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={ackGrade} onChange={(event) => setAckGrade(event.target.checked)} required />
            I have checked these are the same part, not different grades.
          </label>
        ) : null}
        {preview?.conditionsDiffer ? (
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={ackCondition} onChange={(event) => setAckCondition(event.target.checked)} required />
            Condition differs. Merge anyway and keep the survivor condition I selected.
          </label>
        ) : null}

        <label className="block text-sm font-medium">
          Reason
          <textarea
            required
            className="mt-1 w-full rounded-xl border px-3 py-2"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Same part, separate supplier imports"
          />
        </label>

        <div className="flex justify-end gap-2">
          <button type="button" className="rounded-xl border px-4 py-2 text-sm font-semibold" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            disabled={ids.length < 2 || mergeMutation.isPending}
          >
            {mergeMutation.isPending ? 'Merging…' : 'Merge'}
          </button>
        </div>
      </form>
      {modalProps ? <ManagerOverrideModal {...modalProps} /> : null}
    </div>
  );
}
